import { ReactNode, useEffect, useMemo } from 'react';

import { GetUnitReadingsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import maintenanceVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useModulesStore } from '@/stores/modules.store';

import { columns } from './table-meta';

export function ReadingLogTable({
  notFoundDisplay,
  data,
  loading,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  rowSelection,
  onRowSelectionChange,
}: {
  notFoundDisplay: ReactNode;
  data: GetUnitReadingsResponseDTO['data'];
  loading: boolean;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  rowSelection: RowSelectionState;
  onRowSelectionChange: (rowSelection: RowSelectionState) => void;
}) {
  const { syncModuleColumns } = useModulesStore();

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.AssetReadingLogs, columns);
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable
        data={data}
        columns={columns}
        initialSorting={initialSorting}
        onSortingChange={onSortingChange}
        loading={loading}
        notFoundDisplay={notFoundDisplay}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
        grouping={grouping}
        columnOrder={columnOrder.map((col) => col.id)}
        columnVisibility={columnVisibility}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
      />
    </div>
  );
}

export function NoReadingLogsCreatedDisplay() {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={maintenanceVector} alt="ReadingLog" className="h-[170px] w-[250px]" />
      <span className="text-center text-lg leading-[30px] font-medium">This asset has no reading logs yet.</span>
      <div className="text-secondary-text pointer-events-auto flex gap-1 text-center">Reading logs history will appear here as readings are recorded.</div>
    </div>
  );
}

export function NoReadingLogsFoundFromFiltersDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={maintenanceVector} alt="ReadingLog" className="h-28" />
      <span className="text-xl font-medium">No records found</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters or creating a reading log.</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}
